/**
 * React Query hooks for Loans module
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { catalogQueryKeys } from '@/hooks/catalog-hooks';
import {
  loansApi,
  finesApi,
  renewalsApi,
  loanPoliciesApi,
  loanHistoryApi,
  loanStatsApi,
  Loan,
  LoanQueryParams,
  CreateLoanData,
  UpdateLoanData,
  Fine,
  FineQueryParams,
  CreateFineData,
  UpdateFineData,
  LoanRenewal,
  RenewalQueryParams,
  LoanPolicy,
  CreateLoanPolicyData,
  UpdateLoanPolicyData,
  LoanHistoryRecord,
  HistoryQueryParams,
  LoanStatisticsSummary,
} from '@/services/loans-api';

// Query key factories
export const loanQueryKeys = {
  all: ['loans'] as const,
  list: (params?: LoanQueryParams) => ['loans', 'list', params] as const,
  detail: (id: number) => ['loans', 'detail', id] as const,
  pendingRequests: () => ['loans', 'pendingRequests'] as const,
  overdue: () => ['loans', 'overdue'] as const,
  active: () => ['loans', 'active'] as const,
  byBorrower: (borrowerId: number) => ['loans', 'byBorrower', borrowerId] as const,
  canRenew: (id: number) => ['loans', 'canRenew', id] as const,
  fineCalc: (id: number) => ['loans', 'fineCalc', id] as const,
  eligibility: (borrowerId?: number) => ['loans', 'eligibility', borrowerId] as const,
  statistics: () => ['loans', 'statistics'] as const,
  overview: () => ['loans', 'overview'] as const,
};

export const fineQueryKeys = {
  all: ['fines'] as const,
  list: (params?: FineQueryParams) => ['fines', 'list', params] as const,
  detail: (id: number) => ['fines', 'detail', id] as const,
  outstanding: () => ['fines', 'outstanding'] as const,
};

export const renewalQueryKeys = {
  all: ['renewals'] as const,
  list: (params?: RenewalQueryParams) => ['renewals', 'list', params] as const,
  detail: (id: number) => ['renewals', 'detail', id] as const,
};

export const policyQueryKeys = {
  all: ['loanPolicies'] as const,
  list: (params?: { user_type?: string }) => ['loanPolicies', 'list', params] as const,
  detail: (id: number) => ['loanPolicies', 'detail', id] as const,
};

export const historyQueryKeys = {
  all: ['loanHistory'] as const,
  list: (params?: HistoryQueryParams) => ['loanHistory', 'list', params] as const,
  detail: (id: number) => ['loanHistory', 'detail', id] as const,
};

// Loan hooks
export const useLoans = (params?: LoanQueryParams) => {
  return useQuery({
    queryKey: loanQueryKeys.list(params),
    queryFn: () => loansApi.getAll(params).then(r => r.data),
    staleTime: 60_000,
  });
};

export const useLoan = (id?: number) => {
  return useQuery({
    queryKey: id ? loanQueryKeys.detail(id) : ['loans', 'detail', 'undefined'],
    queryFn: () => (id ? loansApi.getById(id).then(r => r.data) : Promise.reject('No loan id')),
    enabled: !!id,
  });
};

export const useCreateLoan = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateLoanData) => loansApi.create(data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: loanQueryKeys.all });
      qc.invalidateQueries({ queryKey: loanQueryKeys.statistics() });
      qc.invalidateQueries({ queryKey: catalogQueryKeys.books.all });
    },
  });
};

/** May this member borrow right now? Re-checked on every pick, so the issue form's
 *  note matches the refusal the desk would get. */
export const useBorrowerEligibility = (borrowerId?: number) => useQuery({
  queryKey: loanQueryKeys.eligibility(borrowerId),
  queryFn: () => loansApi.eligibility(borrowerId!).then(r => r.data),
  enabled: !!borrowerId,
  staleTime: 0,
});

// Pending borrow requests (staff view)
export const usePendingBorrowRequests = () => useQuery({
  queryKey: loanQueryKeys.pendingRequests(),
  queryFn: () => loansApi.getPendingBorrowRequests().then(r => r.data),
  staleTime: 30_000,
});

export const useMyBorrowRequests = () => useQuery({
  queryKey: ['loans', 'my', 'requests'],
  queryFn: () => loansApi.getMyBorrowRequests().then(r => r.data),
  staleTime: 30_000,
});

export const useApproveBorrowRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: number) => loansApi.approveBorrowRequest(requestId).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: loanQueryKeys.pendingRequests() });
      qc.invalidateQueries({ queryKey: loanQueryKeys.all });
      qc.invalidateQueries({ queryKey: loanQueryKeys.statistics() });
    },
  });
};

export const useRejectBorrowRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, reason }: { requestId: number; reason: string }) => loansApi.rejectBorrowRequest(requestId, reason).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: loanQueryKeys.pendingRequests() });
      qc.invalidateQueries({ queryKey: loanQueryKeys.all });
      qc.invalidateQueries({ queryKey: loanQueryKeys.statistics() });
    },
  });
};

// Create borrow request with optimistic update for student's "my requests" cache
export const useCreateBorrowRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { book: number; notes?: string }) => loansApi.createBorrowRequest(payload).then(r => r.data),
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: ['loans', 'my', 'requests'] });
      const previous = qc.getQueryData(['loans', 'my', 'requests']);
      const tempId = -Date.now();
      const tempReq = { id: tempId, book: payload.book, book_title: undefined, borrower: undefined, borrower_name: undefined, status: 'pending', created_at: new Date().toISOString() } as any;
      qc.setQueryData(['loans', 'my', 'requests'], (old: any) => {
        if (!old) return { count: 1, requests: [tempReq] };
        return { ...old, results: [tempReq, ...(old.results || [])], count: (old.count || 0) + 1 };
      });
      return { previous };
    },
    onError: (err, _vars, context: any) => {
      if (context?.previous) qc.setQueryData(['loans', 'my', 'requests'], context.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['loans', 'my', 'requests'] });
      qc.invalidateQueries({ queryKey: loanQueryKeys.all });
      qc.invalidateQueries({ queryKey: loanQueryKeys.statistics() });
    },
  });
};

// Cancel borrow request with optimistic removal from student's requests
export const useCancelBorrowRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: number) => loansApi.cancelBorrowRequest(requestId).then(r => r.data),
    onMutate: async (requestId: number) => {
      await qc.cancelQueries({ queryKey: ['loans', 'my', 'requests'] });
      const previous = qc.getQueryData(['loans', 'my', 'requests']);
      qc.setQueryData(['loans', 'my', 'requests'], (old: any) => {
        if (!old) return old;
        return { ...old, results: (old.results || []).filter((r: any) => r.id !== requestId), count: Math.max(0, (old.count || 1) - 1) };
      });
      return { previous };
    },
    onError: (_err, _vars, context: any) => {
      if (context?.previous) qc.setQueryData(['loans', 'my', 'requests'], context.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['loans', 'my', 'requests'] });
      qc.invalidateQueries({ queryKey: loanQueryKeys.all });
      qc.invalidateQueries({ queryKey: loanQueryKeys.statistics() });
    },
  });
};

export const useUpdateLoan = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateLoanData) => loansApi.update(id, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: loanQueryKeys.detail(id) });
      qc.invalidateQueries({ queryKey: loanQueryKeys.statistics() });
    },
  });
};

export const useRenewLoan = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { reason?: string; new_due_date?: string }) => loansApi.renew(id, payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: loanQueryKeys.detail(id) });
      qc.invalidateQueries({ queryKey: loanQueryKeys.statistics() });
    },
  });
};

export const useRenewLoanGeneric = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: number; reason?: string; new_due_date?: string }) =>
      loansApi.renew(id, payload).then(r => r.data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: loanQueryKeys.detail(variables.id) });
      qc.invalidateQueries({ queryKey: loanQueryKeys.all });
      qc.invalidateQueries({ queryKey: loanQueryKeys.statistics() });
    },
  });
};

export const useReturnBook = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload?: { notes?: string; condition_id?: number }) => loansApi.returnBook(id, payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: loanQueryKeys.detail(id) });
      qc.invalidateQueries({ queryKey: loanQueryKeys.statistics() });
      qc.invalidateQueries({ queryKey: catalogQueryKeys.books.all });
    },
  });
};

export const useReturnBookGeneric = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: number; notes?: string; condition_id?: number }) =>
      loansApi.returnBook(id, payload).then(r => r.data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: loanQueryKeys.detail(variables.id) });
      qc.invalidateQueries({ queryKey: loanQueryKeys.all });
      qc.invalidateQueries({ queryKey: loanQueryKeys.statistics() });
      qc.invalidateQueries({ queryKey: catalogQueryKeys.books.all });
    },
  });
};

export const useLoanCanRenew = (id?: number) => {
  return useQuery({
    queryKey: id ? loanQueryKeys.canRenew(id) : ['loans', 'canRenew', 'undefined'],
    queryFn: () => (id ? loansApi.canRenew(id).then(r => r.data) : Promise.reject('No loan id')),
    enabled: !!id,
  });
};

export const useLoanFineCalculation = (id?: number) => {
  return useQuery({
    queryKey: id ? loanQueryKeys.fineCalc(id) : ['loans', 'fineCalc', 'undefined'],
    queryFn: () => (id ? loansApi.calculateFine(id).then(r => r.data) : Promise.reject('No loan id')),
    enabled: !!id,
    refetchInterval: 60_000, // keep fine updated
  });
};

export const useOverdueLoans = () => useQuery({
  queryKey: loanQueryKeys.overdue(),
  queryFn: () => loansApi.getOverdue().then(r => r.data),
  staleTime: 30_000,
});

export const useActiveLoans = () => useQuery({
  queryKey: loanQueryKeys.active(),
  queryFn: () => loansApi.getActive().then(r => r.data),
  staleTime: 30_000,
});

export const useBorrowerLoans = (borrowerId?: number) => useQuery({
  queryKey: borrowerId ? loanQueryKeys.byBorrower(borrowerId) : ['loans', 'byBorrower', 'undefined'],
  queryFn: () => (borrowerId ? loansApi.getByBorrower(borrowerId).then(r => r.data) : Promise.reject('No borrower id')),
  enabled: !!borrowerId,
});

// Fine hooks
export const useFines = (params?: FineQueryParams) => useQuery({
  queryKey: fineQueryKeys.list(params),
  queryFn: () => finesApi.getAll(params).then(r => r.data),
  staleTime: 60_000,
});

export const useFine = (id?: number) => useQuery({
  queryKey: id ? fineQueryKeys.detail(id) : ['fines', 'detail', 'undefined'],
  queryFn: () => (id ? finesApi.getById(id).then(r => r.data) : Promise.reject('No fine id')),
  enabled: !!id,
});

export const useCreateFine = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFineData) => finesApi.create(data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fineQueryKeys.all });
      qc.invalidateQueries({ queryKey: loanQueryKeys.statistics() });
    },
  });
};

export const useUpdateFine = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateFineData) => finesApi.update(id, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fineQueryKeys.detail(id) });
      qc.invalidateQueries({ queryKey: loanQueryKeys.statistics() });
    },
  });
};

export const usePayFine = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { amount: string; payment_method?: string; notes?: string }) => finesApi.pay(id, payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fineQueryKeys.detail(id) });
      qc.invalidateQueries({ queryKey: fineQueryKeys.outstanding() });
      qc.invalidateQueries({ queryKey: loanQueryKeys.statistics() });
    },
  });
};

export const useWaiveFine = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { reason: string }) => finesApi.waive(id, payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fineQueryKeys.detail(id) });
      qc.invalidateQueries({ queryKey: fineQueryKeys.outstanding() });
      qc.invalidateQueries({ queryKey: loanQueryKeys.statistics() });
    },
  });
};

export const useOutstandingFines = () => useQuery({
  queryKey: fineQueryKeys.outstanding(),
  queryFn: () => finesApi.getOutstanding().then(r => r.data),
  staleTime: 30_000,
});

// Renewal hooks
export const useRenewals = (params?: RenewalQueryParams) => useQuery({
  queryKey: renewalQueryKeys.list(params),
  queryFn: () => renewalsApi.getAll(params).then(r => r.data),
  staleTime: 60_000,
});

export const useRenewal = (id?: number) => useQuery({
  queryKey: id ? renewalQueryKeys.detail(id) : ['renewals', 'detail', 'undefined'],
  queryFn: () => (id ? renewalsApi.getById(id).then(r => r.data) : Promise.reject('No renewal id')),
  enabled: !!id,
});

// Policy hooks
export const useLoanPolicies = (params?: { user_type?: string }) => useQuery({
  queryKey: policyQueryKeys.list(params),
  queryFn: () => loanPoliciesApi.getAll(params).then(r => r.data),
  staleTime: 5 * 60_000,
});

export const useLoanPolicy = (id?: number) => useQuery({
  queryKey: id ? policyQueryKeys.detail(id) : ['loanPolicies', 'detail', 'undefined'],
  queryFn: () => (id ? loanPoliciesApi.getById(id).then(r => r.data) : Promise.reject('No policy id')),
  enabled: !!id,
});

export const useCreateLoanPolicy = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateLoanPolicyData) => loanPoliciesApi.create(data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: policyQueryKeys.all }),
  });
};

export const useUpdateLoanPolicy = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateLoanPolicyData) => loanPoliciesApi.update(id, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: policyQueryKeys.detail(id) });
      qc.invalidateQueries({ queryKey: policyQueryKeys.all });
    },
  });
};

// History hooks
export const useLoanHistory = (params?: HistoryQueryParams) => useQuery({
  queryKey: historyQueryKeys.list(params),
  queryFn: () => loanHistoryApi.getAll(params).then(r => r.data),
  staleTime: 60_000,
});

export const useLoanHistoryRecord = (id?: number) => useQuery({
  queryKey: id ? historyQueryKeys.detail(id) : ['loanHistory', 'detail', 'undefined'],
  queryFn: () => (id ? loanHistoryApi.getById(id).then(r => r.data) : Promise.reject('No history id')),
  enabled: !!id,
});

// Statistics hooks
export const useLoanStatistics = () => useQuery({
  queryKey: loanQueryKeys.statistics(),
  queryFn: () => loanStatsApi.getSummary().then(r => r.data),
  refetchInterval: 60_000,
});

// Prefetch helper
export const usePrefetchLoansData = () => {
  const qc = useQueryClient();
  return () => {
    qc.prefetchQuery({ queryKey: loanQueryKeys.statistics(), queryFn: () => loanStatsApi.getSummary().then(r => r.data) });
    qc.prefetchQuery({ queryKey: loanQueryKeys.overdue(), queryFn: () => loansApi.getOverdue().then(r => r.data) });
  };
};

export type {
  Loan,
  Fine,
  LoanRenewal,
  LoanPolicy,
  LoanHistoryRecord,
  LoanStatisticsSummary,
  CreateLoanData,
  UpdateLoanData,
  CreateFineData,
  UpdateFineData,
  CreateLoanPolicyData,
  UpdateLoanPolicyData,
  LoanQueryParams,
  FineQueryParams,
  RenewalQueryParams,
  HistoryQueryParams,
};
